/**
 * Shared JWT auth utilities — no external dependency required.
 *
 * JWT payloads are base64url-encoded JSON. We decode them with the standard
 * atob() available in every browser, avoiding the need for a package like
 * jwt-decode while achieving the exact same result.
 */

/**
 * Decode the payload of a JWT without verifying its signature.
 * Returns null if the token is malformed.
 */
export function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    // base64url uses - and _ instead of + and /
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/**
 * Return true when the token is missing, malformed, or its `exp` claim is in
 * the past.  `exp` is seconds-since-epoch; Date.now() returns milliseconds.
 *
 *   if (exp * 1000 < Date.now()) → token is expired
 */
export function isTokenExpired(token) {
  if (!token) return true;
  const payload = parseJwt(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return payload.exp * 1000 < Date.now();
}

// ── Site-user session ────────────────────────────────────────────────────────
// The keys every site page reads. Admin and designer portals keep their own.
export const USER_TOKEN_KEY = 'ivisora_auth_token';
export const USER_PROFILE_KEY = 'ivisora_user';
/** Fired on window whenever the stored session is saved or cleared in this tab. */
export const AUTH_CHANGED_EVENT = 'ivisora:auth-changed';

const announce = () => {
  try { window.dispatchEvent(new Event(AUTH_CHANGED_EVENT)); } catch { /* non-browser */ }
};

/** Store a session the server just issued. A call without a token stores nothing. */
export function saveUserSession(user, token) {
  if (!token || !user) return;
  localStorage.setItem(USER_TOKEN_KEY, token);
  localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(user));
  announce();
}

export function clearUserSession() {
  localStorage.removeItem(USER_TOKEN_KEY);
  localStorage.removeItem(USER_PROFILE_KEY);
  announce();
}

/**
 * The signed-in site user, or null. Signed in means BOTH a readable user and
 * an unexpired token; a cached user alone is not a session. An expired session
 * is cleared on the way out so no page keeps trusting it.
 */
export function getValidUserSession() {
  const token = localStorage.getItem(USER_TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    clearUserSession();
    return null;
  }
  if (!token) return null;
  let user = null;
  try { user = JSON.parse(localStorage.getItem(USER_PROFILE_KEY) || 'null'); } catch { user = null; }
  return user ? { user, token } : null;
}

/**
 * Schedule an automatic logout when the JWT's `exp` claim is reached.
 *
 * @param {string}   token       - raw JWT string
 * @param {string[]} storageKeys - localStorage keys to clear on expiry
 * @param {Function} onExpired   - callback run at expiry (e.g. navigate + toast)
 * @returns {number|null}        setTimeout id — store it in a ref so you can
 *                               cancel it on component unmount or manual logout
 */
export function scheduleAutoLogout(token, storageKeys, onExpired) {
  const payload = parseJwt(token);
  if (!payload || typeof payload.exp !== 'number') return null;

  const msUntilExpiry = payload.exp * 1000 - Date.now();
  if (msUntilExpiry <= 0) {
    // Already expired — clear immediately and call back
    storageKeys.forEach(k => localStorage.removeItem(k));
    onExpired();
    return null;
  }

  return setTimeout(() => {
    storageKeys.forEach(k => localStorage.removeItem(k));
    onExpired();
  }, msUntilExpiry);
}
